import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface Particle {
  x: number;
  y: number;
  size: number;
  color: string;
  velocityX: number;
  velocityY: number;
  glowSize: number;
}

const ParticleNetworkAnimation: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const particlesRef = useRef<Particle[]>([]);
  const { theme } = useTheme();

  // Theme-aware colors (memoized for stability)
  const colors = useMemo(() => 
    theme === 'dark' 
      ? ['#14b8a6', '#0d9488', '#3b82f6'] // Teal and blue for dark mode
      : ['#0d9488', '#14b8a6', '#2563eb'], // Darker teal and blue for light mode
    [theme]
  );

  // Calculate optimal particle count based on screen size
  const getParticleCount = useCallback(() => {
    const width = window.innerWidth;
    
    // Simple responsive approach based on screen width
    if (width <= 480) {
      // Mobile phones
      return 25;
    } else if (width <= 768) {
      // Small tablets
      return 50;
    } else if (width <= 1024) {
      // Large tablets
      return 100;
    } else if (width <= 1440) {
      // Small desktop
      return 150;
    } else {
      // Large desktop
      return 200;
    }
  }, []);

  const Particle = useCallback((x: number, y: number, size: number, color: string, velocityX: number, velocityY: number): Particle => {
    return {
      x,
      y,
      size,
      color,
      velocityX,
      velocityY,
      glowSize: Math.random() * 4// Increased glow size (2-6 range)
    };
  }, []);

  const drawParticle = useCallback((particle: Particle, ctx: CanvasRenderingContext2D) => {
    ctx.beginPath();
    ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
    ctx.fillStyle = particle.color;
    ctx.shadowColor = particle.color;
    ctx.shadowBlur = particle.glowSize;
    ctx.fill();
    ctx.shadowBlur = 0; // Reset shadow blur after drawing
  }, []);

  const updateParticle = useCallback((particle: Particle, canvas: HTMLCanvasElement) => {
    particle.x += particle.velocityX;
    particle.y += particle.velocityY;

    // Reverse direction on boundary collision
    if (particle.x < 0 || particle.x > canvas.width) particle.velocityX *= -1;
    if (particle.y < 0 || particle.y > canvas.height) particle.velocityY *= -1;
  }, []);

  const connectParticles = useCallback((ctx: CanvasRenderingContext2D) => {
    const particles = particlesRef.current;
    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x;
        const dy = particles[i].y - particles[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 120) { // Increased connection distance from 100 to 120
          // Create gradient for the line
          const gradient = ctx.createLinearGradient(
            particles[i].x,
            particles[i].y,
            particles[j].x,
            particles[j].y
          );
          gradient.addColorStop(0, particles[i].color);
          gradient.addColorStop(1, particles[j].color);

          ctx.beginPath();
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 0.5; // Increased line width from 0.2 to 0.5
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(particles[j].x, particles[j].y);
          ctx.stroke();
        }
      }
    }
  }, []);

  const initParticles = useCallback((canvas: HTMLCanvasElement) => {
    const count = getParticleCount();
    const width = window.innerWidth;
    
    // Responsive particle size based on screen width
    let sizeRange: [number, number];
    if (width <= 480) {
      sizeRange = [0.5, 1.5]; // Smaller particles for mobile
    } else if (width <= 768) {
      sizeRange = [0.8, 2]; // Medium particles for tablets
    } else {
      sizeRange = [1, 3]; // Larger particles for desktop
    }
    
    particlesRef.current = [];
    for (let i = 0; i < count; i++) {
      const size = Math.random() * (sizeRange[1] - sizeRange[0]) + sizeRange[0];
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const velocityX = (Math.random() - 0.5) * 2; // Slightly slower for smoother movement
      const velocityY = (Math.random() - 0.5) * 2;

      particlesRef.current.push(Particle(x, y, size, color, velocityX, velocityY));
    }
  }, [Particle, colors, getParticleCount]);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    animationRef.current = requestAnimationFrame(animate);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particlesRef.current.forEach((particle) => {
      updateParticle(particle, canvas);
      drawParticle(particle, ctx);
    });

    connectParticles(ctx);
  }, [updateParticle, drawParticle, connectParticles]);

  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    initParticles(canvas);
  }, [initParticles]);

  // Debounced resize handler to prevent too many resize events
  const debouncedResize = useCallback(() => {
    let timeoutId: NodeJS.Timeout;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 250); // 250ms debounce
    };
  }, [handleResize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Initialize canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Initialize particles only once
    if (particlesRef.current.length === 0) {
      initParticles(canvas);
    }

    // Start animation only if not already running
    if (!animationRef.current) {
      animate();
    }

    // Add debounced resize listener
    const debouncedResizeHandler = debouncedResize();
    window.addEventListener('resize', debouncedResizeHandler);

    return () => {
      window.removeEventListener('resize', debouncedResizeHandler);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = undefined;
      }
    };
  }, [debouncedResize]); // Include debouncedResize dependency

  // Update particle colors when theme changes (without reinitializing)
  useEffect(() => {
    if (particlesRef.current.length > 0) {
      particlesRef.current.forEach((particle) => {
        particle.color = colors[Math.floor(Math.random() * colors.length)];
      });
    }
  }, [theme, colors]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full opacity-20"
      style={{ zIndex: 1 }}
    />
  );
};

export default ParticleNetworkAnimation;
